/**
 * S-08 «Способы входа»: список контактов, добавление нового (код на новый
 * контакт) и удаление.
 *
 * BS §5.5: добавление — `POST /me/identities/challenges` → код →
 * `POST /me/identities/challenges/{id}/verify`; операции помечены `x-step-up`,
 * поэтому 403 `step_up_required` ведёт в SYS-06 и повторяет действие после
 * подтверждения. Удалить единственный контакт нельзя (409 `last_identity`).
 */
import { useEffect, useMemo, useRef, useState } from 'react'

import { api, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { Challenge, Identity, IdentityKind } from '@shared/api/types'
import {
  EMPTY_PHONE,
  emailProblem,
  formatPhone,
  phoneProblem,
  toE164,
  type PhoneValue,
} from '@shared/lib/phone'
import {
  Button,
  CodeInput,
  Dialog,
  Field,
  Icon,
  Input,
  PhoneInput,
  SegmentedControl,
  Sheet,
  useToast,
} from '@shared/ui'

import { refreshMe } from '../../boot/bootstrap'
import { formatDateTime } from '../../lib/format'
import { useAppStore } from '../../model/app-store'
import styles from './settings.module.css'

export function IdentitiesSection(): React.ReactElement {
  const me = useAppStore((s) => s.me)
  const toast = useToast()
  const setBlocking = useAppStore((s) => s.setBlocking)

  const identities = me?.identities ?? []
  const [addOpen, setAddOpen] = useState(false)
  const [removeFor, setRemoveFor] = useState<Identity | null>(null)
  const [removing, setRemoving] = useState(false)

  const remove = async (identity: Identity): Promise<void> => {
    setRemoving(true)
    try {
      await api.DELETE('/me/identities/{identityId}', { params: { path: { identityId: identity.id } } })
      await refreshMe()
      setRemoveFor(null)
      toast.show(
        `${identity.kind === 'phone' ? 'Номер' : 'Почта'} ${identity.masked} больше не используется для входа`,
      )
    } catch (error) {
      if (error instanceof ApiError && error.code === 'last_identity') {
        toast.show('Нельзя удалить единственный способ входа — сначала добавьте другой', { tone: 'danger' })
      } else if (error instanceof ApiError && error.code === 'step_up_required') {
        setBlocking({ kind: 'step_up_required', retry: () => void remove(identity) })
      } else {
        toast.show('Не удалось удалить способ входа', { tone: 'danger' })
      }
    } finally {
      setRemoving(false)
    }
  }

  return (
    <>
      <div className={styles['card']}>
        {identities.map((identity) => (
          <div key={identity.id} className={styles['sessionRow']}>
            <span className={styles['identityIcon']}>
              <Icon name={identity.kind === 'phone' ? 'phone' : 'mail'} size={20} />
            </span>

            <div className={styles['sessionBody']}>
              <div className={styles['sessionName']}>{identity.masked}</div>
              <div className={styles['sessionMeta']}>
                {identity.kind === 'phone' ? 'Телефон' : 'Почта'}
                {identity.is_primary ? ' · основной' : ''}
                {` · подтверждён ${formatDateTime(identity.verified_at)}`}
              </div>
            </div>

            {identities.length > 1 ? (
              <Button
                size="sm"
                variant="dangerGhost"
                icon="trash"
                aria-label={`Удалить ${identity.masked}`}
                onClick={() => setRemoveFor(identity)}
              />
            ) : null}
          </div>
        ))}
      </div>

      <p className={styles['footnote']}>
        Код для входа приходит на любой из этих контактов. Оставьте хотя бы два.
      </p>

      <div>
        <Button variant="primary" fullWidth iconStart="plus" onClick={() => setAddOpen(true)}>
          Добавить способ входа
        </Button>
      </div>

      <AddIdentitySheet
        open={addOpen}
        existing={identities}
        onClose={() => setAddOpen(false)}
        onAdded={(identity) => {
          setAddOpen(false)
          toast.show(
            `${identity.kind === 'phone' ? 'Номер' : 'Почта'} ${identity.masked} добавлен${identity.kind === 'phone' ? '' : 'а'}`,
          )
        }}
      />

      {/* Удаление — критическое действие (ПС §04): подтверждаем */}
      <Dialog
        open={removeFor !== null}
        onClose={() => setRemoveFor(null)}
        dismissOnScrim={false}
        title={removeFor?.kind === 'phone' ? 'Удалить номер?' : 'Удалить почту?'}
        description={
          removeFor
            ? `${removeFor.masked} больше не подойдёт для входа. Код будет приходить на оставшиеся контакты.`
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoveFor(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={removing}
              onClick={() => {
                if (removeFor) void remove(removeFor)
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </>
  )
}

/* ===================================================================== */
/* Добавление контакта: контакт → код                                     */
/* ===================================================================== */

function AddIdentitySheet({
  open,
  existing,
  onClose,
  onAdded,
}: {
  open: boolean
  existing: readonly Identity[]
  onClose: () => void
  onAdded: (identity: Identity) => void
}): React.ReactElement {
  const setBlocking = useAppStore((s) => s.setBlocking)

  const [kind, setKind] = useState<IdentityKind>(() =>
    existing.some((i) => i.kind === 'email')
      ? 'phone'
      : existing.some((i) => i.kind === 'phone')
        ? 'email'
        : 'phone',
  )
  const [phone, setPhone] = useState<PhoneValue>(EMPTY_PHONE)
  const [email, setEmail] = useState('')
  const [attempted, setAttempted] = useState(false)
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const identifier =
    kind === 'phone' ? toE164(phone) : emailProblem(email) ? null : email.trim().toLowerCase()
  const localProblem = kind === 'phone' ? phoneProblem(phone) : emailProblem(email)
  const hasInput = kind === 'phone' ? phone.digits.length > 0 : email.trim().length > 0
  const secondsLeft = useCountdown(challenge?.resend_after ?? null)

  // Закрытие листа сбрасывает форму: следующий раз начинается с чистого контакта.
  useEffect(() => {
    if (open) return
    setPhone(EMPTY_PHONE)
    setEmail('')
    setCode('')
    setChallenge(null)
    setError(null)
    setAttempted(false)
  }, [open])

  const requestCode = async (): Promise<void> => {
    setAttempted(true)
    if (!identifier || pending) return
    setPending(true)
    setError(null)

    try {
      const created = unwrap(
        await api.POST('/me/identities/challenges', { body: { kind, identifier } as never }),
      )
      setChallenge(created)
      setCode('')
    } catch (err) {
      if (err instanceof ApiError && err.code === 'step_up_required') {
        setBlocking({ kind: 'step_up_required', retry: () => void requestCode() })
      } else {
        setError(describeRequestError(err, kind))
      }
    } finally {
      setPending(false)
    }
  }

  const verify = async (): Promise<void> => {
    if (!challenge || code.length < 6 || pending) return
    setPending(true)
    setError(null)

    try {
      // Контракт: у подтверждения нового контакта тело — только `code` (сессия уже есть,
      // устройство и способ доставки токена не пересоздаются); сервер отвергает лишние поля.
      const identity = unwrap(
        await api.POST('/me/identities/challenges/{challengeId}/verify', {
          params: { path: { challengeId: challenge.challenge_id } },
          body: { code } as never,
        }),
      )
      await refreshMe()
      onAdded(identity)
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'challenge_expired' || err.code === 'challenge_locked')) {
        // Нужен новый код — возвращаемся к контакту, он уже введён.
        setChallenge(null)
      }
      setError(describeVerifyError(err))
      setCode('')
    } finally {
      setPending(false)
    }
  }

  // Код введён целиком — проверяем сами, кнопка остаётся видимым эквивалентом.
  const submittedCode = useRef<string | null>(null)
  useEffect(() => {
    if (code.length < 6) {
      submittedCode.current = null
      return
    }
    if (!pending && submittedCode.current !== code) {
      submittedCode.current = code
      void verify()
    }
    // verify пересоздаётся каждый рендер; зависимость по коду достаточна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, pending])

  const step: 'contact' | 'code' = challenge ? 'code' : 'contact'

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={step === 'contact' ? 'Новый способ входа' : 'Подтвердите контакт'}
      description={
        step === 'contact'
          ? 'Отправим код на новый контакт — так мы убедимся, что он ваш.'
          : `Код отправлен на ${kind === 'phone' ? formatPhone(phone) : email.trim()}`
      }
      footer={
        step === 'contact' ? (
          <Button
            variant="primary"
            fullWidth
            loading={pending}
            disabled={!hasInput}
            onClick={() => void requestCode()}
          >
            Отправить код
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setChallenge(null)}>
              Изменить контакт
            </Button>
            <Button
              variant="primary"
              loading={pending}
              disabled={code.length < 6}
              onClick={() => void verify()}
            >
              Подтвердить
            </Button>
          </>
        )
      }
    >
      {step === 'contact' ? (
        <div className={styles['formStack']}>
          <SegmentedControl
            value={kind}
            onValueChange={(next) => {
              setKind(next)
              setError(null)
              setAttempted(false)
            }}
            fullWidth
            aria-label="Вид контакта"
            segments={[
              { value: 'phone', label: 'Телефон', icon: 'phone' },
              { value: 'email', label: 'Почта', icon: 'mail' },
            ]}
          />

          {kind === 'phone' ? (
            <Field
              label="Номер телефона"
              error={error ?? (attempted ? localProblem : null)}
              hint="Российский номер: +7 и 10 цифр. Для другой страны начните с «+» и кода страны"
            >
              {({ id, describedBy, invalid }) => (
                <PhoneInput
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  value={phone}
                  onValueChange={setPhone}
                  placeholder="+7 (900) 123-45-67"
                  autoFocus
                />
              )}
            </Field>
          ) : (
            <Field label="Адрес почты" error={error ?? (attempted ? localProblem : null)}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                  autoFocus
                />
              )}
            </Field>
          )}
        </div>
      ) : (
        <div className={styles['formStack']}>
          <Field error={error}>
            {({ id, describedBy, invalid }) => (
              <CodeInput
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={code}
                onValueChange={setCode}
                disabled={pending}
                autoFocus
              />
            )}
          </Field>

          <p className={styles['cardText']}>
            {secondsLeft > 0 ? (
              `Отправить код ещё раз можно через ${secondsLeft} с`
            ) : (
              <Button variant="ghost" size="sm" onClick={() => void requestCode()} disabled={pending}>
                Отправить код ещё раз
              </Button>
            )}
          </p>
        </div>
      )}
    </Sheet>
  )
}

/* ===================================================================== */
/* Тексты ошибок (CC §10.1: решение принимает код, не `message`)          */
/* ===================================================================== */

function describeRequestError(error: unknown, kind: IdentityKind): string {
  if (!(error instanceof ApiError)) return 'Нет соединения. Проверьте сеть и попробуйте снова.'

  switch (error.code) {
    case 'validation_failed':
      return kind === 'phone'
        ? 'Сервер не принял номер. Нужен номер с кодом страны, например +7 (900) 123-45-67.'
        : 'Сервер не принял адрес. Проверьте написание — например name@example.com.'
    case 'identity_exists':
      return 'Этот контакт уже привязан к вашему аккаунту.'
    case 'rate_limited':
      return error.retryAfter
        ? `Слишком много запросов кода. Повторить можно через ${formatSeconds(error.retryAfter)}.`
        : 'Слишком много запросов кода. Подождите несколько минут.'
    default:
      return 'Не удалось отправить код. Попробуйте ещё раз.'
  }
}

function describeVerifyError(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Нет соединения. Проверьте сеть и попробуйте снова.'

  switch (error.code) {
    case 'code_invalid': {
      const left = error.details
        .map((d) => (d as { attempts_left?: unknown }).attempts_left)
        .find((v): v is number => typeof v === 'number')
      return left !== undefined && left > 0 ? `Неверный код. Осталось попыток: ${left}.` : 'Неверный код.'
    }
    case 'identity_exists':
      return 'Этот контакт уже привязан к вашему аккаунту.'
    case 'challenge_expired':
      return 'Код устарел — запросите новый.'
    case 'challenge_locked':
      return 'Попытки исчерпаны — запросите новый код.'
    default:
      return 'Не удалось подтвердить контакт. Попробуйте ещё раз.'
  }
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} с`
  return `${Math.ceil(seconds / 60)} мин`
}

function useCountdown(until: string | null): number {
  const target = useMemo(() => (until ? Date.parse(until) : 0), [until])
  const [left, setLeft] = useState(() => remaining(target))

  useEffect(() => {
    if (!target) return
    setLeft(remaining(target))
    const timer = setInterval(() => {
      const next = remaining(target)
      setLeft(next)
      if (next <= 0) clearInterval(timer)
    }, 1000)
    return () => clearInterval(timer)
  }, [target])

  return left
}

function remaining(target: number): number {
  if (!target) return 0
  return Math.max(0, Math.ceil((target - Date.now()) / 1000))
}
